import React from "react";
import { Row, Col, Input, Modal, Drawer, Spin, message } from "antd";
import { TagOutlined, UserAddOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import closeX from "../../../../assets/images/close-x.svg";
import UserSearch from "../../../../assets/images/mdi_user-search.svg";
import CircleCloseIcon from "../../../../assets/images/remove.svg";
import View from "../../../../assets/images/view.svg";
import EditIcon from "../../../../assets/images/ri_edit-line.svg";
import data from "../../../../constants/retailPos.json";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useCartStore from "../../../../stores/useCartStore";
import { getMultilingualText } from "../../../../utils/getMultilingualText";
import { toCamelCase } from "./customerFormUtils";

const CustomerSearchDrawer = ({
  // Drawer visibility & close
  displayCustomerSearch,
  closeCustomerSearch,
  // Search input
  customerSearchInputRef,
  customerSearchInput,
  setCustomerSearchInput,
  customerSearchResults,
  setCustomerSearchResults,
  // Customer actions
  selectCustomer,
  showAddNewCustomerFields,
  showEditOldCustomerFields,
  // Display state setters
  setDisplayCustomerSearch,
  setDisplayAddNewCustomer,
  setDisplayEditOldCustomer,
  // Keyboard / layout
  setLayoutName,
  setInputName,
  setOpen,
  keyboard,
  setEditFlag,
  setIsViewMode,
  // Edit customer
  setSelectedEditOldCustomer,
  setActiveTabName,
  // Data
  cart,
  tillData,
  posConfig,
  retailCustomerRules,
  retailCustomerFields,
  // Form helpers
  customerForm,
  convertMetaDataToKeyValue,
  setViewCustomerValues,
  // Auto-enable helpers
  autoEnablePanCustomer,
  autoEnableNriBilling,
  // Stock verification & loading
  verifyStock,
  hasShownCustomerModalRef,
  loading,
  setLoading,
  // Keyboard input handler (defined in parent as onChangeInput)
  onChangeInput,
}) => {
  const selectedReturnType = useTransactionStore.getState().selectedReturnType;

  return (
    <Drawer
      open={displayCustomerSearch}
      width="62%"
      title={null}
      closeIcon={null}
      styles={{ header: { height: 0 }, body: { paddingTop: 0, backgroundColor: "#ffffff" } }}
      placement="right"
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      <Row style={{ display: "flex", alignItems: "center", fontSize: "1vw" }}>
        <p style={{ fontSize: "1.5em", fontWeight: "500", color: "#0F0718", flex: 1, marginBottom: 0, marginTop: "2.5vh" }}>{getMultilingualText("Search for Customer")}</p>
        <img
          src={closeX}
          onClick={() => {
            const defaultCustomer = cart?.customer?.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
            const isPreOrderPage = window.location.pathname.includes("/preorder");

            // === PAN RULE CHECK ===
            const panRule = retailCustomerRules?.find((r) => r.action === "Request PAN");
            const exchCart = useCartStore.getState().exchangeCart;
            const panActiveCart = selectedReturnType === "EXCHANGE" && exchCart?.items?.length > 0 ? exchCart : cart;
            const panRuleActive = panRule && Number(panActiveCart?.total) > Number(panRule.value);

            // 1. PAN RULE VIOLATED - TRAP (Close only)
            if (panRuleActive) {
              Modal.confirm({
                title: "Action Required",
                content: "You cannot proceed without selecting a customer.",
                width: 420,
                centered: true,
                closable: false,
                keyboard: false,
                maskClosable: false,
                cancelText: "Close",
                cancelButtonProps: {
                  type: "primary",
                  size: "middle",
                },
                onCancel: () => {},
              });
              setLayoutName("default");
              return;
            }

            // 2. DEFAULT CUSTOMER + "Before Payment" MODE - SHOW "Yes, Proceed"
            //    BUT when customerRequired="Y", never show the proceed option.
            if (defaultCustomer && navigator.onLine && posConfig?.showCustomerSearchValue === "Before Payment") {
              if (isPreOrderPage || posConfig?.customerRequired === "Y") {
                Modal.confirm({
                  title: "Customer not selected",
                  content:
                    posConfig?.customerRequired === "Y"
                      ? "Customer is required to complete this order. Please add a customer."
                      : "Would you like to proceed for payment without adding Customer?",
                  width: 420,
                  centered: true,
                  okButtonProps: { style: { display: "none" } },
                  cancelText: posConfig?.customerRequired === "Y" ? "OK" : "Cancel",
                  onCancel: () => {},
                });
              } else {
                Modal.confirm({
                  title: "Customer not selected",
                  content: "Would you like to proceed for payment without adding Customer?",
                  width: 420,
                  centered: true,
                  okText: "Yes, Proceed",
                  cancelText: "Cancel",
                  onOk: () => {
                    closeCustomerSearch();
                    verifyStock();
                  },
                  onCancel: () => {},
                });
              }
            } else {
              closeCustomerSearch();
            }

            setLayoutName("default");
          }}
          style={{
            marginLeft: "auto",
            cursor: "pointer",
            width: "1.5vw",
            marginTop: "1.5vh",
          }}
        />
      </Row>
      <Row style={{ marginTop: "1.5%", overflow: "hidden" }}>
        <Col span={24} style={{ fontSize: "1vw" }}>
          <Input
            ref={customerSearchInputRef}
            placeholder="Search"
            id="sm-product-search"
            size="large"
            style={data.posScreenStyles.customerModal.customerSearchInput}
            className="customerSearchInput"
            prefix={
              loading ? <Spin size="small" style={{ marginRight: "8px", color: "#black" }} /> : <img src={UserSearch} style={data.posScreenStyles.customerModal.searchIcon} />
            }
            suffix={
              <img
                src={CircleCloseIcon}
                alt=""
                style={data.posScreenStyles.customerModal.searchIcon}
                onClick={() => {
                  setCustomerSearchInput("");
                  setCustomerSearchResults([]);
                  setLoading(false);
                }}
                className="remove_btn"
              />
            }
            value={customerSearchInput}
            onFocus={() => {
              setTimeout(() => {
                customerSearchInputRef.current?.focus();
                setInputName("customerSearch");
                setOpen(true);
                const currentInputValue = customerSearchInput || "";
                keyboard?.current?.setInput(currentInputValue);
              }, 300);
            }}
            onPressEnter={() => {
              if (!loading) {
                if (customerSearchResults?.length === 1) {
                  selectCustomer(0);
                } else if (customerSearchResults?.length === 0 && customerSearchInput.trim() !== "" && !customerSearchInput.startsWith(" ")) {
                  showAddNewCustomerFields();
                }
              }
            }}
            onChange={(e) => {
              if (navigator.onLine) {
                onChangeInput(e);
                if (e.target.value === "") {
                  setCustomerSearchResults([]);
                  setCustomerSearchInput(e.target.value);
                  setLoading(false);
                } else {
                  setCustomerSearchInput(e.target.value);
                }
              } else {
                setCustomerSearchInput(e.target.value);
                setLoading(false);
                message.warning("You are offline. Customer search requires an internet connection.");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: "2%" }}>
        <Col span={20} style={{ fontSize: "1vw" }}>
          {!customerSearchInput || customerSearchInput.trim() === "" ? (
            <div style={data.posScreenStyles.header.left}>
              <div style={data.posScreenStyles.customerModal.customerSearchListEmpty}>
                <Row justify="center" align="middle">
                  <p style={data.posScreenStyles.customerModal.addNewCustomer}>Start typing to search for customers</p>
                </Row>
              </div>
            </div>
          ) : customerSearchResults && customerSearchResults.length > 0 ? (
            <div style={data.posScreenStyles.header.left}>
              <div style={data.posScreenStyles.customerModal.customerSearchList}>
                <Scrollbars style={{ height: "77vh" }}>
                  {customerSearchResults.map((customerSearchResult, index) => (
                    <div
                      tabIndex={0}
                      onKeyPress={(e) => (e.charCode === 13 ? selectCustomer(index) : null)}
                      key={`${index}`}
                      style={data.posScreenStyles.customerModal.customerDropDownDetailsCard}
                    >
                      <Row
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          paddingBottom: "0.1%",
                        }}
                      >
                        <Col span={20} onClick={() => selectCustomer(index)} style={{ cursor: "pointer", padding: "10px" }}>
                          <p style={data.posScreenStyles.customerModal.customerName}>{customerSearchResult.name}</p>

                          {customerSearchResult?.b2cCustomerSegment?.name && (
                            <p
                              style={{
                                fontSize: "1em",
                                color: "#4A4A4A",
                                textAlign: "left",
                                marginBottom: 0,
                                fontWeight: 400,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <TagOutlined style={{ marginRight: 4, fontSize: "12px" }} />
                              {customerSearchResult.b2cCustomerSegment.name}
                            </p>
                          )}

                          {(customerSearchResult.mobileNo || customerSearchResult.email) && (
                            <div
                              style={{
                                display: "flex",
                                gap: "16px",
                                alignItems: "center",
                                marginTop: "0.3vh",
                              }}
                            >
                              {customerSearchResult.mobileNo && (
                                <span
                                  style={{
                                    fontSize: "1em",
                                    color: "#4A4A4A",
                                    fontWeight: 400,
                                  }}
                                >
                                  <PhoneOutlined style={{ marginRight: 4 }} />
                                  {customerSearchResult.mobileNo}
                                </span>
                              )}

                              {customerSearchResult.email && (
                                <span
                                  style={{
                                    fontSize: "1em",
                                    color: "#4A4A4A",
                                    fontWeight: 400,
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <MailOutlined style={{ marginRight: 4 }} />
                                  {customerSearchResult.email}
                                </span>
                              )}
                            </div>
                          )}
                        </Col>

                        <Col
                          span={4}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            padding: "10px",
                          }}
                        >
                          <img
                            onClick={async () => {
                              if (retailCustomerFields && retailCustomerFields.length > 0) {
                                const mappedCustomer = convertMetaDataToKeyValue(customerSearchResult);
                                const loyaltyBalanceField = retailCustomerFields.find((f) => f.name?.toLowerCase().includes("loyalty balance"));
                                const loyaltyBalance = toCamelCase(loyaltyBalanceField?.name);
                                await customerForm.setFieldsValue({
                                  ...mappedCustomer,
                                  mobileno: mappedCustomer?.mobileNo,
                                  lastname: mappedCustomer?.lastName,
                                  lastvisitdate: mappedCustomer?.lastVisitDate,
                                  cardnumber: mappedCustomer?.cardNumber,
                                  city: mappedCustomer?.customerAddress?.city,
                                  taxID: mappedCustomer?.taxId,
                                  anniversaryDate: mappedCustomer?.anniversaryDate?.split(" ")[0],
                                  birthday: mappedCustomer?.birthday?.split(" ")[0],
                                  validity: mappedCustomer?.validity?.split(" ")[0],
                                  loyaltyLevel: mappedCustomer?.loyaltyLevel?.cwrLoyaltyLevelId || null,
                                  [loyaltyBalance]: mappedCustomer?.retlLoyaltyBalance,
                                });
                                autoEnablePanCustomer(mappedCustomer);
                                autoEnableNriBilling(mappedCustomer);
                                setViewCustomerValues(mappedCustomer);
                                setSelectedEditOldCustomer(customerSearchResult);
                                setDisplayEditOldCustomer(true);
                                setDisplayAddNewCustomer(false);
                                setIsViewMode(true);
                              } else {
                                setEditFlag(false);
                                showEditOldCustomerFields(customerSearchResult);
                              }
                            }}
                            src={View}
                            style={{ height: "3vh", marginRight: "5px" }}
                          />

                          <img
                            onClick={async () => {
                              if (retailCustomerFields && retailCustomerFields.length > 0) {
                                const mappedCustomer = convertMetaDataToKeyValue(customerSearchResult);
                                const loyaltyBalanceField = retailCustomerFields.find((f) => f.name?.toLowerCase().includes("loyalty balance"));
                                const loyaltyBalance = toCamelCase(loyaltyBalanceField?.name);
                                await customerForm.setFieldsValue({
                                  ...mappedCustomer,
                                  mobileno: mappedCustomer?.mobileNo,
                                  lastname: mappedCustomer?.lastName,
                                  taxID: mappedCustomer?.taxId,
                                  lastvisitdate: mappedCustomer?.lastVisitDate,
                                  cardnumber: mappedCustomer?.cardNumber,
                                  city: mappedCustomer?.customerAddress?.city,
                                  taxID: mappedCustomer?.taxId,
                                  anniversaryDate: mappedCustomer?.anniversaryDate?.split(" ")[0],
                                  birthday: mappedCustomer?.birthday?.split(" ")[0],
                                  validity: mappedCustomer?.validity?.split(" ")[0],
                                  loyaltyLevel: mappedCustomer?.loyaltyLevel?.cwrLoyaltyLevelId || null,
                                  [loyaltyBalance]: mappedCustomer?.retlLoyaltyBalance,
                                });
                                autoEnablePanCustomer(mappedCustomer);
                                autoEnableNriBilling(mappedCustomer);
                                setViewCustomerValues(mappedCustomer);
                                setSelectedEditOldCustomer(customerSearchResult);
                                setDisplayEditOldCustomer(true);
                                setDisplayAddNewCustomer(false);
                                setEditFlag(true);
                                setIsViewMode(false);
                              } else {
                                showEditOldCustomerFields(customerSearchResult);
                                setEditFlag(true);
                              }
                            }}
                            src={EditIcon}
                            style={{
                              height: "3vh",
                              marginRight: "5px",
                              marginLeft: "0.5vw",
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                </Scrollbars>
              </div>
            </div>
          ) : (
            <div style={data.posScreenStyles.header.left}>
              <div style={data.posScreenStyles.customerModal.customerSearchListEmpty}>
                <Row justify="center" align="middle">
                  <p style={data.posScreenStyles.customerModal.addNewCustomer}>No customers found</p>
                </Row>
              </div>
            </div>
          )}
        </Col>
      </Row>

      {selectedReturnType !== "STORE_CREDIT" && (!customerSearchInput || customerSearchInput.trim() === "" || customerSearchResults?.length === 0) && (
        <>
          <hr style={{ border: "none", borderTop: "1px solid #d1d5db", marginBottom: "16px" }} />

          <Row style={{ fontSize: "1vw", marginTop: "2.7vh" }}>
            <Col span={8}>
              <button
                style={{
                  height: "6vh",
                  backgroundColor: "#2F3856",
                  color: "#fff",
                  fontSize: "1em",
                  border: "none",
                  borderRadius: "5px",
                  width: "90%",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setActiveTabName("contact");
                  showAddNewCustomerFields();
                }}
              >
                <UserAddOutlined style={{ fontSize: "1.2em", color: "#fff", marginRight: "0.5vw" }} />
                Add New Customer
              </button>
            </Col>
          </Row>
        </>
      )}
    </Drawer>
  );
};

export default CustomerSearchDrawer;
